import { zones } from 'tzdata';

export type InputValueType = string | number;

type StringInput = {
	value: string;
	type: 'string';
};

type NumberInput = {
	value: number;
	type: 'number';
};

type OptionInput = {
	value: string;
	type: 'options';
	options: string[];
};

export type InputType = (StringInput | NumberInput | OptionInput)[];

const fullLongMediumShort = ['full', 'long', 'medium', 'short'] as const;
const numericTwoDigit = ['numeric', '2-digit'] as const;
const longShort = ['long', 'short'] as const;
const longShortNarrow = [...longShort, 'narrow'] as const;
const trueFalse = [true, false];
const alwaysAuto = ['always', 'auto'] as const;
const shortLong = ['short', 'long'] as const;
const currency = [
	'AED',
	'AFN',
	'ALL',
	'AMD',
	'ANG',
	'AOA',
	'ARS',
	'AUD',
	'AWG',
	'AZN',
	'BAM',
	'BBD',
	'BDT',
	'BGN',
	'BHD',
	'BIF',
	'BMD',
	'BND',
	'BOB',
	'BOV',
	'BRL',
	'BSD',
	'BTN',
	'BWP',
	'BYN',
	'BZD',
	'CAD',
	'CDF',
	'CHE',
	'CHF',
	'CHW',
	'CLF',
	'CLP',
	'CNY',
	'COP',
	'COU',
	'CRC',
	'CUC',
	'CUP',
	'CVE',
	'CZK',
	'DJF',
	'DKK',
	'DOP',
	'DZD',
	'EGP',
	'ERN',
	'ETB',
	'EUR',
	'FJD',
	'FKP',
	'GBP',
	'GEL',
	'GHS',
	'GIP',
	'GMD',
	'GNF',
	'GTQ',
	'GYD',
	'HKD',
	'HNL',
	'HRK',
	'HTG',
	'HUF',
	'IDR',
	'ILS',
	'INR',
	'IQD',
	'IRR',
	'ISK',
	'JMD',
	'JOD',
	'JPY',
	'KES',
	'KGS',
	'KHR',
	'KMF',
	'KPW',
	'KRW',
	'KWD',
	'KYD',
	'KZT',
	'LAK',
	'LBP',
	'LKR',
	'LRD',
	'LSL',
	'LYD',
	'MAD',
	'MDL',
	'MGA',
	'MKD',
	'MMK',
	'MNT',
	'MOP',
	'MRU',
	'MUR',
	'MVR',
	'MWK',
	'MXN',
	'MXV',
	'MYR',
	'MZN',
	'NAD',
	'NGN',
	'NIO',
	'NOK',
	'NPR',
	'NZD',
	'OMR',
	'PAB',
	'PEN',
	'PGK',
	'PHP',
	'PKR',
	'PLN',
	'PYG',
	'QAR',
	'RON',
	'RSD',
	'RUB',
	'RWF',
	'SAR',
	'SBD',
	'SCR',
	'SDG',
	'SEK',
	'SGD',
	'SHP',
	'SLL',
	'SOS',
	'SRD',
	'SSP',
	'STN',
	'SVC',
	'SYP',
	'SZL',
	'THB',
	'TJS',
	'TMT',
	'TND',
	'TOP',
	'TRY',
	'TTD',
	'TWD',
	'TZS',
	'UAH',
	'UGX',
	'USD',
	'USN',
	'UYI',
	'UYU',
	'UYW',
	'UZS',
	'VES',
	'VND',
	'VUV',
	'WST',
	'XAF',
	'XAG',
	'XAU',
	'XBA',
	'XBB',
	'XBC',
	'XBD',
	'XCD',
	'XDR',
	'XOF',
	'XPD',
	'XPF',
	'XPT',
	'XSU',
	'XTS',
	'XUA',
	'XXX',
	'YER',
	'ZAR',
	'ZMW',
	'ZWL'
] as const;
const zeroToTwenty = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
const oneToTwentyOne = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];
const upperLowerFalse = ['upper', 'lower', 'false'];

const dateStyle = fullLongMediumShort;
const timeStyle = fullLongMediumShort;

export type NullableOptionValue = OptionValue | null;
export type OptionValue = string | boolean | number;

const calendar = [
	'buddhist',
	'chinese',
	'coptic',
	'ethiopia',
	'ethiopic',
	'gregory',
	'hebrew',
	'indian',
	'islamic',
	'iso8601',
	'japanese',
	'persian',
	'roc'
];
const dayPeriod = longShortNarrow;
const numberingSystem = [
	'arab',
	'arabext',
	'bali',
	'beng',
	'deva',
	'fullwide',
	'gujr',
	'guru',
	'hanidec',
	'khmr',
	'knda',
	'laoo',
	'latn',
	'limb',
	'mlym',
	'mong',
	'mymr',
	'orya',
	'tamldec',
	'telu',
	'thai',
	'tibt'
];
const localeMatcher = ['lookup', 'best fit'];
const timeZone = Object.keys(zones);
const hour12 = trueFalse;
const hourCycle = ['h11', 'h12', 'h23', 'h24'];
const formatMatcher = ['basic', 'best fit'];
const weekday = longShortNarrow;
const era = longShortNarrow;
const year = numericTwoDigit;
const month = [...numericTwoDigit, ...longShortNarrow];
const day = numericTwoDigit;
const hour = numericTwoDigit;
const minute = numericTwoDigit;
const second = numericTwoDigit;
const fractionalSecondDigits = [0, 1, 2, 3];
const timeZoneName = longShort;
const listFormatType = ['conjunction', 'disjunction', 'unit'];
const numeric = alwaysAuto;
const style = longShortNarrow;
const compactDisplay = shortLong;
const currencyDisplay = ['symbol', 'narrowSymbol', 'code', 'name'];
const currencySign = ['accounting', 'standard'];
const notation = ['standard', 'scientific', 'engineering', 'compact'];
const signDisplay = ['auto', 'never', 'always', 'exceptZero'];
const unit = [
	'acre',
	'bit',
	'byte',
	'celsius',
	'centimeter',
	'day',
	'degree',
	'fahrenheit',
	'fluid-ounce',
	'foot',
	'gallon',
	'gigabit',
	'gigabyte',
	'gram',
	'hectare',
	'hour',
	'inch',
	'kilobit',
	'kilobyte',
	'kilogram',
	'kilometer',
	'liter',
	'megabit',
	'megabyte',
	'meter',
	'mile',
	'mile-scandinavian',
	'milliliter',
	'millimeter',
	'millisecond',
	'minute',
	'month',
	'ounce',
	'percent',
	'petabyte',
	'pound',
	'second',
	'stone',
	'terabit',
	'terabyte',
	'week',
	'yard',
	'year'
];
const unitDisplay = longShortNarrow;
const useGrouping = trueFalse;
const numberStyle = ['decimal', 'currency', 'percent', 'unit'];
const collation = [
	'big5han',
	'dict',
	'direct',
	'ducet',
	'gb2312',
	'phonebk',
	'phonetic',
	'pinyin',
	'reformed',
	'searchjl',
	'stroke',
	'trad',
	'unihan'
];
const sensitivity = ['base', 'accent', 'case', 'variant'];
const sortSearch = ['sort', 'search'];
const cardinalOrdinal = ['cardinal', 'ordinal'];
const displayNamesType = ['language', 'region', 'script', 'currency'];
const codeNone = ['code', 'none'];

export const dateGenericFormatOptions = {
	calendar,
	dayPeriod,
	numberingSystem,
	localeMatcher,
	timeZone,
	hour12,
	hourCycle,
	formatMatcher,
	weekday,
	era,
	year,
	month,
	day,
	hour,
	minute,
	second,
	fractionalSecondDigits,
	timeZoneName
};

export const dateTimeFormatOptions = {
	dateStyle,
	timeStyle,
	...dateGenericFormatOptions,
};

export const dateFormatOptions = {
	dateStyle,
	...dateGenericFormatOptions
}

export const timeFormatOptions = {
	timeStyle,
	...dateGenericFormatOptions
}

export const listFormatOptions = {
	localeMatcher,
	type: listFormatType,
	style
};

export const relativeTimeFormatOptions = {
	localeMatcher,
	numeric,
	style
};

export const relativeTimeFormatOptionsWithNumberingSystem = {
	...relativeTimeFormatOptions,
	numberingSystem
};

export const relativeTimeOptions = [
	'year',
	'quarter',
	'month',
	'week',
	'day',
	'hour',
	'minute',
	'second'
];

export const numberFormatOptions = {
	compactDisplay,
	currency,
	currencyDisplay,
	currencySign,
	localeMatcher,
	notation,
	numberingSystem,
	signDisplay,
	style: numberStyle,
	unit,
	unitDisplay,
	useGrouping,
	minimumIntegerDigits: oneToTwentyOne,
	minimumFractionDigits: zeroToTwenty,
	maximumFractionDigits: zeroToTwenty,
	minimumSignificantDigits: oneToTwentyOne,
	maximumSignificantDigits: oneToTwentyOne
};

export const collateOptions = {
	localeMatcher,
	usage: sortSearch,
	sensitivity,
	ignorePunctuation: trueFalse,
	numeric: trueFalse,
	caseFirst: upperLowerFalse,
	collation
};

export const pluralRulesOptions = {
	localeMatcher,
	type: cardinalOrdinal,
	minimumIntegerDigits: oneToTwentyOne,
	minimumFractionDigits: zeroToTwenty,
	maximumFractionDigits: zeroToTwenty,
	minimumSignificantDigits: oneToTwentyOne,
	maximumSignificantDigits: oneToTwentyOne
};

export const displayNamesOptions = {
	localeMatcher,
	style: longShortNarrow,
	type: displayNamesType,
	fallback: codeNone
};
